import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outBack, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Vela, the Archer without arrows (models/vela.ts, docs/ANTIHEROES.md): on the fallen sentinel's bones like Ser
 * Corvin, so she walks, reels and falls as the sentinel does. Her own: the shot (bow up, the empty string drawn to
 * the cheek and loosed at 0.13 s), the smoke arrow (a deeper, slower draw), the leap back (a crouch and a spring
 * away), the channel of the Endless rain (the bow raised to the sky, drawn and held, trembling) and the rain itself
 * (loosed straight up).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on the
 * trunk leans it forward, +Y turns the trunk to its left. The bow hangs from the `shield` bone: +X on it brings the
 * bow back upright when the arm swings forward.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** The bow levelled at the target, the string drawn back to the cheek. */
const DRAWN: P = {
  root: [0, 0.25, 0, 0, -0.03, 0],
  body: [0, 0.3, 0],
  chest: [-0.05, 0.35, 0],
  head: [0, -0.55, 0],
  armL: [-1.45, 0, 0.25],
  foreL: [0, 0, 0],
  shield: [1.35, 0, 0],
  armR: [-1.45, 0, -0.35],
  foreR: [-2.1, 0, 0],
  sword: [0, 0, 0],
  legL: [-0.25, 0, -0.1],
  shinL: [0.2, 0, 0],
  legR: [0.2, 0, 0.1],
  shinR: [0.25, 0, 0],
};
/** Loosed: the drawing hand flung back open, the bow arm still. */
const LOOSED: P = { ...DRAWN, armR: [-1.2, 0, -0.9], foreR: [-0.6, 0, 0], chest: [-0.08, 0.4, 0] };

const attack = (): Clip<B> =>
  clip<B>('attack', 1.0, false, (b, t) => {
    timeline(b, t, [
      [0, { ...DRAWN, foreR: [-1.6, 0, 0] }],
      [0.1, DRAWN, outQuad],
      [0.13, LOOSED, outExpo],
      [0.45, LOOSED, outCubic],
      [1.0, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.015 * kick(t, 0.13, 6, 9));
  }, undefined, 'Disparo');

/** The smoke arrow: a slow, deep draw, held a beat, loosed with the whole body. */
const arrow = (): Clip<B> =>
  clip<B>('arrow', 1.4, false, (b, t) => {
    const deep: P = { ...DRAWN, chest: [-0.12, 0.5, 0], armR: [-1.5, 0, -0.5], foreR: [-2.4, 0, 0], legL: [-0.4, 0, -0.1], shinL: [0.35, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.35, deep, outCubic],
      [0.55, deep],
      [0.6, { ...LOOSED, chest: [0.05, 0.45, 0] }, outExpo],
      [0.95, LOOSED],
      [1.4, {}, inOut],
    ], { head: 0.05, flapB: 0.1 });
  }, undefined, 'Flecha de humo');

/** The leap back: a crouch, a spring up and away, the mantle trailing; landing low. */
const leap = (): Clip<B> =>
  clip<B>('leap', 0.9, false, (b, t) => {
    const crouch: P = { root: [0, 0, 0, 0, -0.25, 0], body: [0.35, 0, 0], chest: [0.2, 0, 0], legL: [-0.9, 0, 0], shinL: [1.4, 0, 0], legR: [-0.7, 0, 0], shinR: [1.3, 0, 0], armL: [-0.6, 0, 0.3], shield: [0.4, 0, 0], armR: [0.4, 0, -0.3] };
    const air: P = { root: [-0.35, 0, 0, 0, 0.45, -0.2], body: [-0.25, 0, 0], chest: [-0.2, 0, 0], head: [0.25, 0, 0], legL: [-0.4, 0, 0], shinL: [0.9, 0, 0], legR: [0.2, 0, 0], shinR: [0.6, 0, 0], armL: [-1.0, 0, 0.6], shield: [0.8, 0, 0], armR: [-0.4, 0, -0.9], flapF: [-0.6, 0, 0], flapB: [0.7, 0, 0] };
    const land: P = { root: [0.1, 0, 0, 0, -0.2, -0.05], body: [0.3, 0, 0], chest: [0.15, 0, 0], legL: [-0.8, 0, 0], shinL: [1.2, 0, 0], legR: [-0.3, 0, 0], shinR: [0.9, 0, 0], armL: [-0.8, 0, 0.3], shield: [0.6, 0, 0], armR: [0.2, 0, -0.5] };
    timeline(b, t, [
      [0, {}],
      [0.12, crouch, outCubic],
      [0.35, air, outQuad],
      [0.62, land, inCubic],
      [0.9, {}, outBack],
    ], { head: 0.04, flapF: 0.06, flapB: 0.06 });
  }, undefined, 'Salto atrás');

/** The Endless rain, channelled: the bow raised to the sky, drawn and held, trembling with it. */
const SKY: P = {
  root: [0, 0, 0, 0, -0.06, 0],
  body: [-0.15, 0.2, 0],
  chest: [-0.4, 0.25, 0],
  head: [-0.6, -0.2, 0],
  armL: [-2.7, 0, 0.15],
  foreL: [0, 0, 0],
  shield: [0.4, 0, 0],
  armR: [-2.5, 0, -0.25],
  foreR: [-1.9, 0, 0],
  legL: [-0.25, 0, -0.12],
  shinL: [0.3, 0, 0],
  legR: [0.25, 0, 0.12],
  shinR: [0.35, 0, 0],
};
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.025;
    for (const [k, v] of Object.entries(SKY) as [B, readonly number[]][]) b[k].rot((v[0] ?? 0) + (k === 'armL' || k === 'chest' ? shake : 0), v[1] ?? 0, v[2] ?? 0);
    b.root.pos(0, -0.06 + 0.01 * Math.sin(p * 2), 0);
    b.flapF.rot(0.1 * Math.sin(p * 3));
    b.flapB.rot(-0.15 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.005;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The rain loosed: the string let go at the sky, the body thrown back with it. */
const rain = (): Clip<B> =>
  clip<B>('rain', 1.5, false, (b, t) => {
    const let_: P = { ...SKY, armR: [-2.0, 0, -0.9], foreR: [-0.4, 0, 0], chest: [-0.5, 0.3, 0], root: [0, 0, 0, 0, -0.03, -0.04] };
    timeline(b, t, [
      [0, SKY],
      [0.12, let_, outExpo],
      [0.8, let_],
      [1.5, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, undefined, 'Lluvia sin fin');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with her own shot and powers in place of the bash. */
export const velaClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), arrow(), leap(), channel(), rain()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { vela: velaClips };
